import { useState, type JSX } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import {
  useAdminTeamSettings,
  useAdminTeams,
  useUpdateTeamSettings,
  type AdminTeamSettings,
  type TeamSettingsPatch,
} from '@/hooks/useAdmin';
import { teamLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';

interface ToggleRule {
  key: 'clippingResolveRequired' | 'screenshotRequired' | 'multipleNichesAllowed' | 'canDeleteInvites' | 'bulkImportEnabled';
  label: string;
  /** What the rule does, shown under the heading. */
  about: string;
  on: string;
  off: string;
}

const RULES: ToggleRule[] = [
  {
    key: 'clippingResolveRequired',
    label: 'Auto-check with Clipping',
    about:
      "Each morning, check this team's invites against Clipping and move them to Joined or Posted when the creator joins or posts.",
    on: 'Checked every morning; joins and posts update on their own.',
    off: "Not checked. Invites stay as Replied (shown as Manual tracking) until an admin updates them.",
  },
  {
    key: 'screenshotRequired',
    label: 'Proof screenshot required',
    about: "Whether outreachers must attach a screenshot of the creator's reply to log an invite.",
    on: 'A screenshot is required to log an invite.',
    off: 'A screenshot is optional.',
  },
  {
    key: 'multipleNichesAllowed',
    label: 'Several niches per invite',
    about: 'Whether an invite can be tagged with more than one niche. Only the first niche counts toward niche bonuses.',
    on: 'Outreachers can pick several niches.',
    off: 'Outreachers pick one niche.',
  },
  {
    key: 'canDeleteInvites',
    label: 'Outreachers can hide invites',
    about:
      'Whether outreachers can hide their own invites. Hidden invites stay visible to admins, and anything owed on them stays owed.',
    on: 'Outreachers can hide their own invites.',
    off: "Outreachers can't hide invites. Admins can still reject one, which writes off what's owed.",
  },
  {
    key: 'bulkImportEnabled',
    label: 'Bulk import',
    about:
      'Whether outreachers can upload a CSV or Excel list of creators and mark them all as messaged or log their replies at once. You can also allow or block one outreacher on their profile.',
    on: 'Outreachers can bulk import, unless blocked on their profile.',
    off: "Outreachers can't bulk import, unless allowed on their profile.",
  },
];

export function AdminTeamSettings(): JSX.Element {
  const query = useAdminTeamSettings();
  const teams = useAdminTeams();
  const update = useUpdateTeamSettings();

  const labelFor = (code: string): string =>
    teamLabel(code, teams.data?.find((t) => t.code === code)?.displayName);

  if (query.isLoading) {
    return (
      <div className="flex items-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load team rules{query.error ? `: ${(query.error as Error).message}` : '.'}
      </p>
    );
  }

  async function save(row: AdminTeamSettings, body: TeamSettingsPatch, done: string): Promise<void> {
    try {
      await update.mutateAsync({ team: row.team, body });
      toast.success(`${labelFor(row.team)}: ${done}`);
    } catch (err) {
      toast.error(`Couldn't save ${labelFor(row.team)}'s rules: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Team rules"
        description="Rules for every outreacher on a team. Each change asks you to confirm, then applies straight away."
      />

      {RULES.map((rule) => (
        <Card key={rule.key}>
          <CardHeader className="pb-3">
            <CardTitle>{rule.label}</CardTitle>
            <p className="max-w-[65ch] text-sm text-muted-foreground">{rule.about}</p>
          </CardHeader>
          <CardContent>
            <table className="responsive-stack w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="pb-2">Team</th>
                  <th className="pb-2">Setting</th>
                </tr>
              </thead>
              <tbody>
                {query.data.map((row) => {
                  // `=== true`: older backends don't send newer rules yet.
                  const enabled = row[rule.key] === true;
                  const team = labelFor(row.team);
                  return (
                    <tr key={`${row.id}-${rule.key}`} className="border-t transition-colors hover:bg-accent/40">
                      <td className="py-2 font-medium">{team}</td>
                      <td className="py-2" data-label="Setting">
                        <div className="flex items-center gap-2">
                          <ConfirmDialog
                            title={`${enabled ? 'Turn off' : 'Turn on'} “${rule.label}” for ${team}?`}
                            description={enabled ? rule.off : rule.on}
                            confirmLabel={enabled ? 'Turn off' : 'Turn on'}
                            pending={update.isPending}
                            onConfirm={() =>
                              save(row, { [rule.key]: !enabled }, `${rule.label.toLowerCase()} ${enabled ? 'off' : 'on'}`)
                            }
                            trigger={
                              <button
                                type="button"
                                role="switch"
                                aria-checked={enabled}
                                aria-label={`${rule.label} for ${team}`}
                                disabled={update.isPending}
                                className={cn(
                                  'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60',
                                  enabled ? 'bg-primary' : 'bg-muted',
                                )}
                              >
                                <span
                                  className={cn(
                                    'inline-block h-5 w-5 transform rounded-full bg-background shadow-sm transition-transform',
                                    enabled ? 'translate-x-5' : 'translate-x-0.5',
                                  )}
                                />
                              </button>
                            }
                          />
                          <span className="text-xs text-muted-foreground">{enabled ? rule.on : rule.off}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Re-log window</CardTitle>
          <p className="max-w-[65ch] text-sm text-muted-foreground">
            Days before an invite that hasn&apos;t joined expires and anyone can log the creator again. Joined and
            posted creators stay with their outreacher for good. Leave it empty to never expire invites.
          </p>
        </CardHeader>
        <CardContent>
          <table className="responsive-stack w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="pb-2">Team</th>
                <th className="pb-2">Days</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((row) => (
                <ReLogRow
                  key={row.id}
                  row={row}
                  label={labelFor(row.team)}
                  pending={update.isPending}
                  onSave={(days) =>
                    save(row, { reOutreachCooldownDays: days }, days ? `re-log window ${days} days` : 're-log window off')
                  }
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Credit window</CardTitle>
          <p className="max-w-[65ch] text-sm text-muted-foreground">
            Hours before logging that a join or post still counts. It covers the gap between a creator joining or
            posting and the outreacher logging them; anything earlier predates the outreach and isn&apos;t credited.
            The default is 24 hours.
          </p>
        </CardHeader>
        <CardContent>
          <table className="responsive-stack w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="pb-2">Team</th>
                <th className="pb-2">Hours</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((row) => (
                <CreditRow
                  key={row.id}
                  row={row}
                  label={labelFor(row.team)}
                  pending={update.isPending}
                  onSave={(hours) => save(row, { attributionBufferHours: hours }, `credit window ${hours} hours`)}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const inputClass =
  'h-8 w-20 rounded-md border border-input bg-transparent px-2 text-sm tabular-nums focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 pointer-coarse:h-11 pointer-coarse:text-base';

function ReLogRow({
  row,
  label,
  pending,
  onSave,
}: {
  row: AdminTeamSettings;
  label: string;
  pending: boolean;
  onSave: (days: number | null) => Promise<void>;
}): JSX.Element {
  const current = row.reOutreachCooldownDays != null && row.reOutreachCooldownDays > 0 ? row.reOutreachCooldownDays : null;
  const [draft, setDraft] = useState(current != null ? String(current) : '');
  const parsed = draft.trim() ? Number.parseInt(draft, 10) : null;
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0 && parsed <= 365);
  const next = parsed && parsed > 0 ? parsed : null;
  const changed = valid && next !== current;
  const id = `relog-${row.id}`;

  return (
    <tr className="border-t transition-colors hover:bg-accent/40">
      <td className="py-2 font-medium">{label}</td>
      <td className="py-2" data-label="Days">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={id} className="sr-only">
            Re-log window in days for {label}
          </label>
          <input
            id={id}
            type="number"
            min={0}
            max={365}
            placeholder="Never"
            value={draft}
            disabled={pending}
            onChange={(e) => setDraft(e.target.value)}
            className={inputClass}
          />
          {changed && (
            <ConfirmDialog
              title={`Change ${label}'s re-log window?`}
              description={
                next
                  ? `Invites on ${label} that haven't joined will expire ${next} day${next === 1 ? '' : 's'} after they were logged, and anyone can then log the creator again. This applies to existing invites from the next daily check.`
                  : `Invites on ${label} will no longer expire. A creator stays with whoever logged them first.`
              }
              confirmLabel="Save"
              pending={pending}
              onConfirm={() => onSave(next)}
              trigger={
                <Button size="sm" disabled={pending}>
                  Save
                </Button>
              }
            />
          )}
          {!valid && <span className="text-xs text-destructive">Enter 0–365 days.</span>}
          <span className="text-xs text-muted-foreground">
            {current
              ? `${current} day${current === 1 ? '' : 's'}: invites that haven't joined expire after this`
              : "Off: invites don't expire"}
          </span>
        </div>
      </td>
    </tr>
  );
}

function CreditRow({
  row,
  label,
  pending,
  onSave,
}: {
  row: AdminTeamSettings;
  label: string;
  pending: boolean;
  onSave: (hours: number) => Promise<void>;
}): JSX.Element {
  const current = row.attributionBufferHours;
  const [draft, setDraft] = useState(String(current));
  const parsed = draft.trim() ? Number.parseInt(draft, 10) : null;
  const valid = parsed != null && Number.isFinite(parsed) && parsed >= 0 && parsed <= 8760;
  const changed = valid && parsed !== current;
  const id = `credit-${row.id}`;

  return (
    <tr className="border-t transition-colors hover:bg-accent/40">
      <td className="py-2 font-medium">{label}</td>
      <td className="py-2" data-label="Hours">
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={id} className="sr-only">
            Credit window in hours for {label}
          </label>
          <input
            id={id}
            type="number"
            min={0}
            max={8760}
            value={draft}
            disabled={pending}
            onChange={(e) => setDraft(e.target.value)}
            className={inputClass}
          />
          {changed && (
            <ConfirmDialog
              title={`Change ${label}'s credit window to ${parsed} hours?`}
              description={`A join or post on ${label} will count if it happened up to ${parsed} hours before the invite was logged. This changes which joins and posts are credited to invites, and so what they earn.`}
              confirmLabel="Save"
              pending={pending}
              onConfirm={() => onSave(parsed!)}
              trigger={
                <Button size="sm" disabled={pending}>
                  Save
                </Button>
              }
            />
          )}
          {!valid && <span className="text-xs text-destructive">Enter 0–8,760 hours.</span>}
          <span className="text-xs text-muted-foreground">
            {current} hour{current === 1 ? '' : 's'} before logging{current === 24 ? ' (default)' : ''}
          </span>
        </div>
      </td>
    </tr>
  );
}
